import * as Sentry from '@sentry/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';

import { apiClient } from '../axios.js';
import { type ErrorResponseData, extractMessageFromResponseData } from '../lib/error.js';
import type { Command } from '../types';
import type { SignalType } from '../types';
import { type Suppression, suppressionListSchema } from '../types/suppression.js';

interface GetSuppressionsResponse {
  suppressions: Suppression[];
}

export type UseGetSuppressionsParams = {
  deviceId: string;
  detectionGroupId?: string;
  detectionGroupCreatedAt?: string;
  enabled?: boolean;
};

export function useGetSuppressions({
  deviceId,
  detectionGroupId,
  detectionGroupCreatedAt,
  enabled = true,
}: UseGetSuppressionsParams) {
  const hasDetectionGroupFilter = Boolean(detectionGroupId || detectionGroupCreatedAt);
  const hasRequiredDetectionGroupParams = Boolean(detectionGroupId && detectionGroupCreatedAt);
  const shouldFetch =
    Boolean(deviceId) &&
    (!hasDetectionGroupFilter ? enabled : Boolean(hasRequiredDetectionGroupParams && enabled));

  return useQuery({
    queryKey: ['suppressions', { deviceId, detectionGroupId, detectionGroupCreatedAt }],
    queryFn: async (): Promise<GetSuppressionsResponse> => {
      const response = await apiClient.get(`/devices/${deviceId}/suppressions`, {
        params: {
          deviceId,
          detectionGroupId,
          detectionGroupCreatedAt,
          sort: 'started_at',
          direction: 'desc',
        },
      });
      return suppressionListSchema.parse(response.data);
    },
    enabled: shouldFetch,
  });
}

export type SuppressionChannelPayload = {
  channelFrequency: number;
  detectionGroupCreatedAt?: string;
  detectionGroupId?: string;
  duration?: number;
  offset?: number;
  bandwidth?: number;
  parentDetectionGroupCreatedAt?: string;
  parentDetectionGroupId?: string;
  priority?: number;
  type: SignalType;
  isManual?: boolean;
};

export type StopSuppressionChannelPayload = {
  id?: string;
  channelFrequency: number;
  type: SignalType;
  detectionGroupId?: string;
  detectionGroupCreatedAt?: string;
};

export type ChangeSuppressionFrequencyPayload = {
  channelFrequency: number;
  type: SignalType;
  duration: number;
  detectionGroupId?: string;
  detectionGroupCreatedAt?: string;
};

type CreateSuppressionsPayload = {
  deviceId: string;
  payload: { channels: SuppressionChannelPayload[] };
  targets?: string[];
};

export type CreateSuppressionError = Error & { status?: number; serverMessage?: string };

type StopSuppressionsPayload = {
  deviceId: string;
  payload: { channels: StopSuppressionChannelPayload[] };
};

type ChangeSuppressionFrequencyRequest = {
  deviceId: string;
  payload: ChangeSuppressionFrequencyPayload;
};

export const useCreateSuppression = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: CreateSuppressionsPayload): Promise<Command> => {
      try {
        const { data } = await apiClient.post(`/devices/${payload.deviceId}/suppressions`, payload);
        return data;
      } catch (error) {
        Sentry.captureException(error);
        console.debug({ error });
        const thrown: CreateSuppressionError = new Error('Failed to create suppression.');
        thrown.status = axios.isAxiosError(error) ? error.response?.status : undefined;
        // Read straight off the body rather than via `extractErrorMessage`, whose `error.message`
        // fallback would surface axios's untranslated "Request failed with status code 400" in
        // place of the consumer's localized fallback. A missing response, or a body with no usable
        // message, both leave this `undefined`.
        thrown.serverMessage =
          axios.isAxiosError(error) && error.response
            ? (extractMessageFromResponseData(error.response.data as ErrorResponseData) ??
              undefined)
            : undefined;
        throw thrown;
      }
    },
    onSuccess: (suppression) => {
      queryClient.invalidateQueries({ queryKey: ['suppressions'] });

      if (suppression.deviceId) {
        queryClient.invalidateQueries({
          queryKey: ['suppressions', { deviceId: suppression.deviceId }],
        });
        queryClient.invalidateQueries({
          queryKey: ['detection-groups', { deviceId: suppression.deviceId }],
        });
        queryClient.invalidateQueries({
          predicate: (query) => {
            const key = query.queryKey;
            if (!Array.isArray(key)) return false;
            if (key[0] !== 'all-suppressions') return false;
            const params = (key[1] ?? {}) as { deviceId?: string };
            return params.deviceId === suppression.deviceId;
          },
        });
      }
    },
  });
};

export const useStopSuppression = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (payload: StopSuppressionsPayload): Promise<Command> => {
      try {
        const { data } = await apiClient.post(
          `/devices/${payload.deviceId}/suppressions/stop`,
          payload,
        );
        return data;
      } catch (error) {
        Sentry.captureException(error);
        console.debug({ error });
        throw new Error('Failed to stop suppression.');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      queryClient.invalidateQueries({
        queryKey: ['suppressions', { deviceId: variables.deviceId }],
      });
      queryClient.invalidateQueries({
        predicate: (query) => {
          const key = query.queryKey;
          if (!Array.isArray(key)) return false;
          if (key[0] !== 'all-suppressions') return false;
          const params = (key[1] ?? {}) as { deviceId?: string };
          return params.deviceId === variables.deviceId;
        },
      });
    },
  });
};

export const useChangeSuppressionFrequency = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: ChangeSuppressionFrequencyRequest): Promise<void> => {
      try {
        await apiClient.post(`/devices/${request.deviceId}/suppressions/change-frequency`, {
          payload: request.payload,
        });
      } catch (error) {
        Sentry.captureException(error);
        throw new Error('Failed to change suppression frequency.');
      }
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ['suppressions'] });
      queryClient.invalidateQueries({
        queryKey: ['suppressions', { deviceId: variables.deviceId }],
      });
      queryClient.invalidateQueries({
        queryKey: ['detection-groups', { deviceId: variables.deviceId }],
      });
    },
  });
};
